// ============================================
// Loan Page - New Style
// ============================================

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft, FaFileUpload, FaIdCard, FaUniversity, FaHome, FaChartBar, FaEye, FaFileAlt } from 'react-icons/fa';
import api from '../services/api';
import './Transaction.css';

const Loan = () => {
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [duration, setDuration] = useState('3');
  const [purpose, setPurpose] = useState('');
  const [idCard, setIdCard] = useState(null);
  const [bankBook, setBankBook] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [loading, setLoading] = useState(false);

  const [idCardPreview, setIdCardPreview] = useState(null);
  const [bankBookPreview, setBankBookPreview] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState(null);

  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('ไฟล์ต้องมีขนาดไม่เกิน 5MB');
        return;
      }

      setFile(file);
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const loanAmount = parseFloat(amount);
    if (!loanAmount || loanAmount < 1000) {
      toast.error('จำนวนเงินกู้ขั้นต่ำคือ $1,000');
      return;
    }

    if (!purpose.trim()) {
      toast.error('กรุณาระบุวัตถุประสงค์การกู้');
      return;
    }

    if (!idCard || !bankBook || !selfie) {
      toast.error('กรุณาอัปโหลดเอกสารให้ครบถ้วน');
      return;
    }

    setLoading(true);
    try {
      const uploadedFiles = [];
      
      const idFormData = new FormData();
      idFormData.append('document', idCard);
      const idResponse = await api.uploadLoanDocument(idFormData);
      uploadedFiles.push({
        type: 'idCard',
        name: 'บัตรประชาชน',
        url: idResponse.fileUrl,
        fileName: idResponse.fileName
      });

      const bankFormData = new FormData();
      bankFormData.append('document', bankBook);
      const bankResponse = await api.uploadLoanDocument(bankFormData);
      uploadedFiles.push({
        type: 'bankBook',
        name: 'สำเนาบัญชีธนาคาร',
        url: bankResponse.fileUrl,
        fileName: bankResponse.fileName
      });

      const selfieFormData = new FormData();
      selfieFormData.append('document', selfie);
      const selfieResponse = await api.uploadLoanDocument(selfieFormData);
      uploadedFiles.push({
        type: 'selfie',
        name: 'รูปถ่ายคู่บัตร',
        url: selfieResponse.fileUrl,
        fileName: selfieResponse.fileName
      });

      const loanData = {
        amount: loanAmount,
        duration: parseInt(duration),
        purpose: purpose.trim(),
        documentUrl: JSON.stringify(uploadedFiles),
        documentType: 'multiple',
        documentName: `${uploadedFiles.length} ไฟล์`
      };

      await api.requestLoan(loanData);
      toast.success('ส่งคำขอกู้เงินสำเร็จ กรุณารอแอดมินตรวจสอบเอกสาร');
      navigate('/profile');
    } catch (error) {
      toast.error(error.message || 'ไม่สามารถส่งคำขอได้');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="transaction-page">
      {/* Header */}
      <div className="transaction-header">
        <button className="btn-back" onClick={() => navigate('/profile')}>
          <FaArrowLeft />
        </button>
        <h1>
          <FaUniversity /> ขอกู้เงิน
        </h1>
      </div>

      <div className="transaction-content">
        <div className="transaction-card">
          <div className="info-box info">
            <h3>📋 เอกสารที่ต้องใช้:</h3>
            <ul>
              <li>✓ สำเนาบัตรประชาชน / Passport</li>
              <li>✓ สำเนาหน้าบัญชีธนาคาร</li>
              <li>✓ รูปถ่ายคู่บัตรประชาชน (Selfie)</li>
            </ul>
            <p style={{ marginTop: '12px' }}><strong>📌 หมายเหตุ:</strong></p>
            <ul style={{ marginTop: '4px' }}>
              <li>• ไฟล์แต่ละไฟล์ต้องไม่เกิน 5MB</li>
              <li>• รองรับไฟล์ JPG, PNG, PDF</li>
              <li>• ระยะเวลาตรวจสอบ: 1-3 วันทำการ</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit} className="transaction-form loan-form">
            <div className="form-group">
              <label>จำนวนเงินที่ต้องการกู้ (USD)</label>
              <input
                type="number"
                className="input"
                placeholder="ขั้นต่ำ $1,000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                disabled={loading}
                min="1000"
                step="100"
                required
              />
            </div>

            <div className="form-group">
              <label>ระยะเวลาผ่อนชำระ</label>
              <select
                className="input"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                disabled={loading}
              >
                <option value="3">3 เดือน (ดอกเบี้ย 2%)</option>
                <option value="6">6 เดือน (ดอกเบี้ย 3%)</option>
                <option value="12">12 เดือน (ดอกเบี้ย 5%)</option>
                <option value="24">24 เดือน (ดอกเบี้ย 8%)</option>
              </select>
            </div>

            <div className="form-group">
              <label>วัตถุประสงค์การกู้</label>
              <textarea
                className="input"
                placeholder="เช่น: เพื่อลงทุนธุรกิจ, ใช้จ่ายส่วนตัว, ฉุกเฉิน..."
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                disabled={loading}
                rows="3"
                required
              />
            </div>

            <div className="form-group upload-group">
              <label>
                <FaIdCard /> บัตรประชาชน / Passport
              </label>
              <input
                type="file"
                className="input-file"
                onChange={(e) => handleFileChange(e, setIdCard, setIdCardPreview)}
                accept="image/*,.pdf"
                disabled={loading}
                required
              />
              {idCardPreview && (
                <div className="file-preview">
                  <img src={idCardPreview} alt="ID Card Preview" />
                </div>
              )}
            </div>

            <div className="form-group upload-group">
              <label>
                <FaUniversity /> สำเนาหน้าบัญชีธนาคาร
              </label>
              <input
                type="file"
                className="input-file"
                onChange={(e) => handleFileChange(e, setBankBook, setBankBookPreview)}
                accept="image/*,.pdf"
                disabled={loading}
                required
              />
              {bankBookPreview && (
                <div className="file-preview">
                  <img src={bankBookPreview} alt="Bank Book Preview" />
                </div>
              )}
            </div>

            <div className="form-group upload-group">
              <label>
                <FaFileUpload /> รูปถ่ายคู่บัตรประชาชน (Selfie)
              </label>
              <input
                type="file"
                className="input-file"
                onChange={(e) => handleFileChange(e, setSelfie, setSelfiePreview)}
                accept="image/*"
                disabled={loading}
                required
              />
              {selfiePreview && (
                <div className="file-preview">
                  <img src={selfiePreview} alt="Selfie Preview" />
                </div>
              )}
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading}
            >
              {loading ? 'กำลังส่งคำขอ...' : 'ส่งคำขอกู้เงิน'}
            </button>
          </form>
        </div>
      </div>

      {/* Bottom Navigation */}
      <nav className="bottom-nav">
        <Link to="/" className="nav-item">
          <FaHome />
          <span>Home</span>
        </Link>
        <Link to="/trading" className="nav-item">
          <FaChartBar />
          <span>Trade</span>
        </Link>
        <Link to="/history" className="nav-item">
          <FaEye />
          <span>History</span>
        </Link>
        <Link to="/profile" className="nav-item">
          <FaFileAlt />
          <span>Profile</span>
        </Link>
      </nav>
    </div>
  );
};

export default Loan;
